/**
 * Shared hook for frame footer actions (hide, delete); pinning is
 * `lib/frame-pinning.ts`.
 *
 * Used by frame components and their menus to wire these actions with
 * consistent behavior.
 */

import { useCallback } from "react";
import { usePresentationStore } from "@/stores/presentation-store";
import { useAgentStore } from "@/stores/agent-store";
import { calculateConversationFocusBoxes } from "@/lib/conversation-focus-layout-geometry";
import { removeConversationCompanionFocusFrame } from "@/lib/conversation-companion-focus-layout";
import { applyFocusLayoutToFrames } from "@/lib/focus-window-presentation";
import { currentFocusSurfaceBox } from "@/lib/focus/focus-surface";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { dismissFrameFromFocusPresentation } from "@/lib/focus-frame-dismissal";
import { isTransientConversationFrameMetadata } from "@/lib/transient-conversation-frame";
import { setFrameHidden } from "@/lib/frame-visibility";
import { discardFrames } from "@/lib/frame-ingest";
import { useFramesStore } from "@/stores/frames-store";
import { useFrameField } from "@/stores/frames-selectors";

interface FrameActions {
  handleHide: () => void;
  handleDelete: () => void;
  /** Resolved agent display name (from agentId UUID), or undefined if no agent. */
  agentName: string | undefined;
}

function frameIsTemporary(frameId: string): boolean {
  const frame = useFramesStore.getState().framesById[frameId];
  return !!frame && isTransientConversationFrameMetadata(frame.metadata);
}

export function hideFrameFromCanvas(frameId: string): void {
  const companionLayout =
    usePresentationStore.getState().presentation?.customFocusLayout;
  if (companionLayout?.kind === "conversation_companion_stack") {
    if (frameId === companionLayout.conversationFrameId) {
      exitPresentationMode();
    } else if (removeConversationCompanionFocusFrame(frameId)) {
      if (frameIsTemporary(frameId)) discardFrames([frameId]);
      else setFrameHidden(frameId, true);
      return;
    }
  }
  if (frameIsTemporary(frameId)) {
    const presentation = usePresentationStore.getState().presentation;
    if (presentation?.frameIds.includes(frameId)) {
      const { framesById } = useFramesStore.getState();
      const remainingFocusFrameIds =
        presentation.variant === "focus"
          ? presentation.frameIds.filter(
              (id) => id !== frameId && !!framesById[id],
            )
          : [];
      if (remainingFocusFrameIds.length > 0) {
        discardFrames([frameId]);
        const nextPresentation = usePresentationStore.getState().presentation;
        if (nextPresentation?.variant === "focus") {
          const activeId =
            nextPresentation.frameIds[nextPresentation.activeIndex];
          if (activeId) {
            const keepsConversationColumns =
              nextPresentation.customFocusLayout?.kind ===
              "conversation_columns";
            const boxes = keepsConversationColumns
              ? calculateConversationFocusBoxes(
                  currentFocusSurfaceBox(),
                  nextPresentation.frameIds.length,
                )
              : undefined;
            applyFocusLayoutToFrames(activeId, {
              boxes,
              preserveCustomFocusLayout: keepsConversationColumns,
            });
          }
        }
        return;
      }
      exitPresentationMode();
      return;
    }
    discardFrames([frameId]);
    return;
  }
  setFrameHidden(frameId, true);
}

export function useFrameActions(frameId: string): FrameActions {
  const handleDelete = useCallback(
    () => hideFrameFromCanvas(frameId),
    [frameId],
  );

  const handleHide = useCallback(() => {
    if (!dismissFrameFromFocusPresentation(frameId)) {
      hideFrameFromCanvas(frameId);
    }
  }, [frameId]);

  // Resolve agentId UUID → display name via agent store
  const agentId = useFrameField(frameId, (frame) => frame?.agentId ?? null);
  const agentName = useAgentStore((s) => {
    if (!agentId) return undefined;
    const agent = s.agents.find((a) => a.agent_template === agentId);
    return agent?.template_name ?? undefined;
  });

  return {
    handleHide,
    handleDelete,
    agentName,
  };
}
